// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { trackAsImpersonation, trackGaProductNavButtonEvent } from "@stoneage/App/Utilities/DataLayer";
import Button, { ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import LazyImage from "@stoneage/MobiusOverrides/LazyImage";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    title = "title",
    titleStyle = "titleStyle",
    copy = "copy",
    buttonText = "buttonText",
    buttonStyle = "buttonStyle",
    url = "url",
    openInNewTab = "openInNewTab",
    colorTheme = "colorTheme",
    showCTA = "showCTA",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;

        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.copy]: string;
        [fields.buttonText]: string;
        [fields.buttonStyle]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.showCTA]: boolean;
        [fields.colorTheme]: "light" | "dark";
    };
}

type Props = OwnProps;

export interface InfoImageStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    copy?: TypographyProps;
    button?: ButtonPresentationProps;
    buttonContainer?: InjectableCss;
    imageContainer?: InjectableCss;
    innerTextWrapper?: InjectableCss;
}

export const infoImageStyles: InfoImageStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
            max-width: 100%;

            ${({ showCTA }: any) => {
                if (showCTA) {
                    return `
                    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
                    `;
                }
            }}
        `,
    },
    imageContainer: {
        css: css`
            width: 100%;
            height: 160px;
            &,
            > div {
                max-width: 100%;
            }
        `,
    },
    title: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    copy: {
        css: css`
            margin-bottom: 20px;
            line-height: 26px;

            > p:last-child {
                margin-bottom: 0;
            }
        `,
    },
    button: {},
    buttonContainer: {
        css: css`
            margin-top: auto;
        `,
    },
    innerTextWrapper: {
        css: css`
            ${({ showCTA }: any) => {
                if (showCTA) {
                    return `
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                `;
                }
                if (!showCTA) {
                    return `
                    padding-top: 20px;
                `;
                }
            }}
        `,
    },
};

const styles = infoImageStyles;

const InfoImage = ({
    id,
    fields: { title, titleStyle, copy, buttonText, buttonStyle, url, openInNewTab, colorTheme, imageUrl, showCTA },
}: Props) => {
    const linkUrl = useGetLink(url);

    const getTitleColor = () => (colorTheme === "light" ? "common.black" : "common.white");

    const getCopyColor = () => (colorTheme === "light" ? "text.main" : "common.white");

    const copyText = parse(copy, parserOptions);
    const impersonation = useAppSelector(state => trackAsImpersonation(state));
    const template = (
        <>
            {imageUrl?.length > 0 && (
                <StyledWrapper {...styles.imageContainer}>
                    <LazyImage
                        src={imageUrl}
                        altText={imageUrl}
                        isCatalogImage={false}
                        height="160"
                        cloudinaryWidth="418"
                    />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.innerTextWrapper} {...({ showCTA } as any)}>
                {title?.length > 0 && (
                    <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography} color={getTitleColor()}>
                        {parse(title, parserOptions)}
                    </Typography>
                )}
                {copyText && copy.length > 0 && (
                    <Typography {...styles.copy} color={getCopyColor()}>
                        {copyText}
                    </Typography>
                )}
                {showCTA && linkUrl?.url && (
                    <StyledWrapper {...styles.buttonContainer}>
                        {buttonStyle === "button" ? (
                            <Button
                                variant="primary"
                                href={linkUrl?.url}
                                {...styles.button}
                                target={openInNewTab ? "_blank" : ""}
                                onClick={() => {
                                    trackGaProductNavButtonEvent(impersonation, linkUrl?.url);
                                }}
                            >
                                {buttonText || linkUrl?.title}
                            </Button>
                        ) : (
                            <Link
                                href={linkUrl?.url}
                                color={getCopyColor()}
                                target={openInNewTab ? "_blank" : ""}
                                onClick={() => {
                                    trackGaProductNavButtonEvent(impersonation, linkUrl?.url);
                                }}
                                css={css`
                                    > span {
                                        display: flex;
                                        align-items: center;
                                        font-size: 15px;
                                        font-weight: bold;
                                        line-height: 26px;
                                    }
                                `}
                            >
                                {buttonText || linkUrl?.title}&nbsp;&nbsp;
                                <Icon src="ChevronRight" size={15} color={getCopyColor()}></Icon>
                            </Link>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );

    return (
        <StyledWrapper className="info-image" {...styles.container} {...({ showCTA } as any)}>
            {showCTA ? (
                template
            ) : (
                <Link
                    href={linkUrl?.url}
                    target={openInNewTab ? "_blank" : ""}
                    onClick={() => {
                        trackGaProductNavButtonEvent(impersonation, linkUrl?.url);
                    }}
                >
                    {template}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoImage,
    definition: {
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.copy,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.showCTA,
                displayName: "Show CTA",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.buttonText,
                displayName: "CTA Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.showCTA === true,
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.buttonStyle,
                displayName: "CTA Style",
                editorTemplate: "DropDownField",
                defaultValue: "primary",
                fieldType: "General",
                isVisible: item => item.fields.showCTA === true,
                options: [
                    { displayName: "link", value: "link" },
                    { displayName: "button", value: "button" },
                ],
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
        ],
    },
};

export default widgetModule;
